/**
 * ObliqueSliceViewport2D - Oblique slicing along a centerline.
 *
 * Subscribes to CurrentLocationService ACTIVE_CENTERLINE_CHANGED event.
 * When the active centerline or current location changes, positions the
 * slice plane perpendicular to the centerline at the closest node.
 */

import { SliceViewport2D } from './slice-viewport-2d';
import type { ViewportOptions, ViewportType, ViewportVtkContext } from './types';
import type { ServiceContext } from '../core/types';
import type { Centerline, CenterlineNode } from '../domain/centerline';
import type { Volume } from '../domain/volume';
import { CurrentLocationService } from '../services/current-location-service';
import type { CurrentLocation } from '../services/current-location-service';

/**
 * Widest interactive zoom-out for an oblique cut, as a multiple of the
 * reference scale. Tighter than the orthogonal default: the reference here is
 * the whole volume projected onto the cut plane, and a vessel cross-section
 * stays readable well before that extent fills the viewport.
 *
 * An explicit field of view wider than this cap (the 80 mm default, or the
 * 90–160 mm report snapshots request) is still honoured: fitToViewport never
 * clamps, and the framing becomes the zoom-out limit itself.
 */
const OBLIQUE_MAX_ZOOM_OUT_FACTOR = 0.3;

export class ObliqueSliceViewport2D extends SliceViewport2D {
    /** When set, the viewport zooms to show a square of this size (in mm). When null, fits the full projected volume extent. */
    public initialFieldOfViewMm: number | null = 80;

    private activeCenterline: Centerline | null = null;
    private currentNodeIndex: number = 0;

    protected override get maxZoomOutFactor(): number {
        return OBLIQUE_MAX_ZOOM_OUT_FACTOR;
    }

    getActiveCenterline(): Centerline | null {
        return this.activeCenterline;
    }

    getActiveCenterlineId(): string | null {
        return this.activeCenterline?.id ?? null;
    }

    constructor(
        id: string,
        type: ViewportType,
        vtkContext: ViewportVtkContext,
        context: ServiceContext,
        options?: ViewportOptions
    ) {
        super(id, type, vtkContext, context, options);
        this.subscribeToActiveCenterline();
    }

    // ========================================================================
    // Active centerline subscription
    // ========================================================================

    private subscribeToActiveCenterline(): void {
        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        const existing = locationService.getActiveCenterline();
        if (existing) {
            this.onActiveCenterlineChanged(existing);
        }

        this.unsubscribers.push(
            locationService.subscribe<Centerline | null>(
                locationService.EVENTS.ACTIVE_CENTERLINE_CHANGED,
                (centerline) => this.onActiveCenterlineChanged(centerline)
            )
        );
    }

    private onActiveCenterlineChanged(centerline: Centerline | null): void {
        this.activeCenterline = centerline;

        if (!centerline) return;

        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );
        const location = locationService.getLocation();

        let nodeIndex: number;
        if (location?.centerlinePosition?.centerline === centerline) {
            nodeIndex = location.centerlinePosition.nodeIndex;
        } else if (location) {
            nodeIndex = centerline.findClosestPoint(location.worldPosition).nodeIndex;
        } else {
            nodeIndex = Math.round(centerline.nodeCount / 2);
        }

        const node = centerline.getNodeAt(nodeIndex);
        this.positionFromNode(node, nodeIndex);
        this.fitDefaultZoom(node);
        this.render();
    }

    // ========================================================================
    // Location change override
    // ========================================================================

    protected override onLocationChanged(location: CurrentLocation): void {
        if (this._isUpdatingSlice) return;

        if (!this.activeCenterline) {
            super.onLocationChanged(location);
            return;
        }

        if (location.centerlinePosition?.centerline === this.activeCenterline) {
            this.setSliceIndex(location.centerlinePosition.nodeIndex);
        } else {
            const result = this.activeCenterline.findClosestPoint(location.worldPosition);
            this.setSliceIndex(result.nodeIndex);
        }
    }

    // ========================================================================
    // Slice plane + camera positioning from centerline node
    // ========================================================================

    private positionFromNode(node: CenterlineNode, nodeIndex: number): void {
        this.currentNodeIndex = nodeIndex;
        // Slice plane: perpendicular to the vessel at this node
        this.slicePlane.setOrigin(node.point[0], node.point[1], node.point[2]);
        this.slicePlane.setNormal(node.normal[0], node.normal[1], node.normal[2]);

        // Camera: looking along the vessel normal, with node.up as view up
        const camera = this.getRenderer(0).getActiveCamera();
        const distance = 500;

        camera.setPosition(
            node.point[0] + node.normal[0] * distance,
            node.point[1] + node.normal[1] * distance,
            node.point[2] + node.normal[2] * distance
        );
        camera.setFocalPoint(node.point[0], node.point[1], node.point[2]);
        camera.setViewUp(node.up[0], node.up[1], node.up[2]);

        this.updateCameraClippingRange();
    }

    // ========================================================================
    // Default zoom fitting
    // ========================================================================

    private fitDefaultZoom(node: CenterlineNode): void {
        // Zoom bounds are anchored to the full projected extent, never to the
        // initial field of view — that framing is a working zoom level, and
        // using it as the reference would put the zoom-in floor right at it.
        const { width, height } = this.computeProjectedExtent(node);
        this.setZoomReference(width, height);

        if (this.initialFieldOfViewMm !== null && this.initialFieldOfViewMm > 0) {
            this.fitToViewport(this.initialFieldOfViewMm, this.initialFieldOfViewMm);
        } else {
            this.fitToViewport(width, height);
        }
    }

    /**
     * Set the square field of view (mm) and immediately re-fit the camera.
     * Persists in initialFieldOfViewMm so later centerline-driven refits keep it.
     */
    public setFieldOfViewMm(mm: number): void {
        if (!Number.isFinite(mm) || mm <= 0) return;
        this.initialFieldOfViewMm = mm;
        this.fitToViewport(mm, mm);
        this.render();
    }

    private computeProjectedExtent(node: CenterlineNode): { width: number; height: number } {
        if (!this.volume) return { width: 0, height: 0 };

        const bounds = this.volume.bounds;
        const right = node.right;
        const up = node.up;

        // Project the 8 corners of the volume bounding box onto the view plane
        let minR = Infinity,
            maxR = -Infinity;
        let minU = Infinity,
            maxU = -Infinity;

        for (let xi = 0; xi <= 1; xi++) {
            for (let yi = 0; yi <= 1; yi++) {
                for (let zi = 0; zi <= 1; zi++) {
                    const x = xi === 0 ? bounds[0] : bounds[1];
                    const y = yi === 0 ? bounds[2] : bounds[3];
                    const z = zi === 0 ? bounds[4] : bounds[5];

                    const projR = x * right[0] + y * right[1] + z * right[2];
                    const projU = x * up[0] + y * up[1] + z * up[2];

                    if (projR < minR) minR = projR;
                    if (projR > maxR) maxR = projR;
                    if (projU < minU) minU = projU;
                    if (projU > maxU) maxU = projU;
                }
            }
        }

        return { width: maxR - minR, height: maxU - minU };
    }

    // ========================================================================
    // Slice index API overrides (centerline-driven)
    // ========================================================================

    override get sliceCount(): number {
        if (this.activeCenterline) {
            return this.activeCenterline.nodeCount;
        }
        return super.sliceCount;
    }

    override get sliceIndex(): number {
        if (this.activeCenterline) {
            return this.currentNodeIndex;
        }
        return super.sliceIndex;
    }

    override setSliceIndex(index: number): void {
        if (!this.activeCenterline) {
            super.setSliceIndex(index);
            return;
        }

        const count = this.activeCenterline.nodeCount;
        if (count === 0) return;
        const clamped = Math.max(0, Math.min(index, count - 1));

        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        this._isUpdatingSlice = true;
        try {
            const node = this.activeCenterline.getNodeAt(clamped);
            this.positionFromNode(node, clamped);
            this.render();
            locationService.setCurrentCenterlinePosition(this.activeCenterline.id, clamped);
        } finally {
            this._isUpdatingSlice = false;
        }
    }

    // ========================================================================
    // Slice orientation — driven by centerline, not by volume axes
    // ========================================================================

    protected configureSliceOrientation(volume: Volume): void {
        // Anchor the zoom bounds on the bounding box diagonal, which upper-bounds
        // the extent of any oblique cut. Without this the viewport would have no
        // reference — and so no zoom limits — until a centerline arrives.
        // fitDefaultZoom then replaces it with the exact projected extent.
        const b = volume.bounds;
        const diagonal = Math.hypot(b[1] - b[0], b[3] - b[2], b[5] - b[4]);
        this.setZoomReference(diagonal, diagonal);

        // Initial orientation will be set when the active centerline arrives.
        // If a centerline is already active, position from it now.
        if (this.activeCenterline) {
            const locationService = this.context.services.getService<CurrentLocationService>(
                CurrentLocationService.SERVICE_NAME
            );
            const location = locationService.getLocation();

            let nodeIndex: number;
            if (location?.centerlinePosition?.centerline === this.activeCenterline) {
                nodeIndex = location.centerlinePosition.nodeIndex;
            } else if (location) {
                nodeIndex = this.activeCenterline.findClosestPoint(location.worldPosition).nodeIndex;
            } else {
                nodeIndex = this.activeCenterline.findClosestPoint(volume.center).nodeIndex;
            }

            const node = this.activeCenterline.getNodeAt(nodeIndex);
            this.positionFromNode(node, nodeIndex);
            this.fitDefaultZoom(node);
        }
    }
}
